<script lang="ts">
import { captureException } from '@@/bits/error_tracker'
import { __ } from '@@/bits/intl'
import { getPostPropertyFormattedValue } from '@@/bits/post_properties'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Post, PostColor, PostCustomPropertyValue, WallCustomPostProperty } from '@@/types'
import { storeToRefs } from 'pinia'
import type { Ref, VNodeData } from 'vue'
import { computed, onMounted, ref } from 'vue'
import { defineAsyncComponent } from '@@/bits/vue'

const SurfacePostPropertiesEmailFieldValue = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostPropertiesEmailFieldValue.vue'),
)
const SurfacePostPropertiesLinkButton = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostPropertiesLinkButton.vue'),
)
const SurfacePostPropertiesPhoneNumberFieldValue = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostPropertiesPhoneNumberFieldValue.vue'),
)
const SurfacePostPropertiesTextFieldValue = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostPropertiesTextFieldValue.vue'),
)
const SurfacePostPropertiesUrlFieldValue = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostPropertiesUrlFieldValue.vue'),
)
const SurfacePostPropertiesUserFieldValue = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfacePostPropertiesUserFieldValue.vue'),
)

export interface PostPropertyDisplayAttributes {
  id: string
  name: string
  formattedValue: string
  rawValue?: PostCustomPropertyValue
  dataType?: WallCustomPostProperty['data_type']
}

export type CustomPropertyTextRefs = Record<'customPropertyLabelRefs' | 'customPropertyValueRefs', Ref<HTMLElement[]>>
export default {}
</script>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    source?: 'post' | 'slideshow' | 'expanded_view'
    darkMode: boolean
    locationName?: string
    wallCustomPostProperties: WallCustomPostProperty[]
    customPropertyValues: Post['custom_properties']
    propertyLabelClass?: string
    propertyValueClass?: string
    linkButtonSize?: 24 | 32 | 40 | 56
    postColor: PostColor
    colorScheme: 'light' | 'dark'
  }>(),
  {
    source: 'post',
    locationName: undefined,
    propertyLabelClass: 'text-body-extra-small',
    propertyValueClass: 'text-body-small',
    linkButtonSize: 24,
  },
)

const emit = defineEmits<{
  (name: 'dblclick', event: MouseEvent): void
  (name: 'update:text-element-refs', event: CustomPropertyTextRefs)
}>()

const { customLinkButtonProperty } = storeToRefs(useSurfacePostPropertiesStore())

// Values used for displaying custom post property values
const customPostProperties = computed<PostPropertyDisplayAttributes[]>(() => {
  const customPropertyValues = props.customPropertyValues
  if (customPropertyValues == null) return []
  return props.wallCustomPostProperties
    .filter((property) => property.id != null && property.data_type !== 'link_button') // exclude link_button as it will be rendered separately
    .map((property) => {
      return {
        id: property.id!, // non-null assert: already filtered out cases where property.id is blank
        name: property.name,
        formattedValue: getPostPropertyFormattedValue({ property, value: customPropertyValues[property.id!] }),
        rawValue: customPropertyValues[property.id!],
        dataType: property.data_type,
      }
    })
    .filter(({ formattedValue }) => formattedValue !== '')
})

// Full list of property values: location property + custom properties
const postProperties = computed<PostPropertyDisplayAttributes[]>(() => {
  const properties: PostPropertyDisplayAttributes[] = []
  if (props.locationName) {
    // TODO: display location as a clickable link, using bits/location#navigateToMap
    properties.push({
      id: 'post-property-location',
      name: __('Location'),
      formattedValue: props.locationName,
    })
  }

  properties.push(...customPostProperties.value)
  return properties
})

const customPropertyLabelRefs: Ref<HTMLElement[]> = ref([])
const customPropertyValueRefs: Ref<HTMLElement[]> = ref([])

const addLabelRef: VNodeData['ref'] = (e): void => {
  customPropertyLabelRefs.value.push(e as HTMLElement)
}

const addValueRef: VNodeData['ref'] = (e): void => {
  // ref can be an html element or a vue component
  const ref = e instanceof Element ? e : e?.$el
  if (ref != null) customPropertyValueRefs.value.push(ref as HTMLElement)
}

// Link button-related logic
const linkButtonPropertyValue = computed<string | undefined>(() => {
  if (customLinkButtonProperty.value?.id == null || props.customPropertyValues == null) return undefined

  const linkButtonUrl = props.customPropertyValues[customLinkButtonProperty.value.id] as string | undefined

  if (linkButtonUrl == null || linkButtonUrl === '') return undefined

  const parsedButtonUrl = (() => {
    try {
      return new URL(linkButtonUrl).href
    } catch (_err) {
      // First attempt failed, try adding 'https://' prefix
      try {
        return new URL(`https://${linkButtonUrl}`).href
      } catch (err) {
        captureException(err)
        return undefined
      }
    }
  })()

  return parsedButtonUrl
})

onMounted(() => {
  // For slideshow resizing logic
  emit('update:text-element-refs', {
    customPropertyLabelRefs,
    customPropertyValueRefs,
  })
})
</script>

<template>
  <dl class="flex flex-col gap-1 break-words whitespace-break-spaces" @dblclick="$emit('dblclick', $event)">
    <div
      v-for="prop in postProperties"
      :key="prop.id"
      :class="['flex flex-col py-1', prop.dataType === 'single_select' ? 'gap-1' : 'gap-0.5']"
    >
      <dt
        :ref="addLabelRef"
        :class="[
          props.propertyLabelClass,
          props.darkMode && 'text-light-text-200',
          !props.darkMode && 'text-dark-text-200',
        ]"
        data-testid="surfacePostPropertiesFieldName"
        v-text="prop.name"
      />
      <SurfacePostPropertiesTextFieldValue
        v-if="prop.dataType === 'text'"
        :ref="addValueRef"
        :post-property="prop"
        :dark-mode="darkMode"
        :property-value-class="propertyValueClass"
        data-testid="surfacePostPropertiesValue"
      />
      <SurfacePostPropertiesEmailFieldValue
        v-else-if="prop.dataType === 'email'"
        :ref="addValueRef"
        :post-property="prop"
        :dark-mode="darkMode"
        :property-value-class="propertyValueClass"
      />
      <SurfacePostPropertiesUrlFieldValue
        v-else-if="prop.dataType === 'url'"
        :ref="addValueRef"
        :post-property="prop"
        :dark-mode="darkMode"
        :property-value-class="propertyValueClass"
      />
      <SurfacePostPropertiesPhoneNumberFieldValue
        v-else-if="prop.dataType === 'phone_number'"
        :ref="addValueRef"
        :post-property="prop"
        :dark-mode="darkMode"
        :property-value-class="propertyValueClass"
      />
      <SurfacePostPropertiesUserFieldValue
        v-else-if="prop.dataType === 'user'"
        :ref="addValueRef"
        :post-property="prop"
        :dark-mode="darkMode"
        :property-value-class="propertyValueClass"
      />
      <dd
        v-else
        :ref="addValueRef"
        :class="[
          props.propertyValueClass,
          props.darkMode && 'text-light-text-100',
          !props.darkMode && 'text-dark-text-100',
          prop.dataType === 'single_select' && [
            'rounded-lg px-2 py-0.5 w-fit break-word-anywhere',
            props.source === 'post' && {
              'bg-oceanic-200': postColor === 'blue' && colorScheme === 'light',
              'bg-oceanic-800': postColor === 'blue' && colorScheme === 'dark',
              'bg-park-200': postColor === 'green' && colorScheme === 'light',
              'bg-park-800': postColor === 'green' && colorScheme === 'dark',
              'bg-canary-200 ': postColor === 'orange' && colorScheme === 'light',
              'bg-tangerine-800': postColor === 'orange' && colorScheme === 'dark',
              'bg-grape-200': postColor === 'purple' && colorScheme === 'light',
              'bg-grape-800': postColor === 'purple' && colorScheme === 'dark',
              'bg-scarlet-200 ': postColor === 'red' && colorScheme === 'light',
              'bg-scarlet-800': postColor === 'red' && colorScheme === 'dark',
              'bg-grey-100': postColor === null && colorScheme === 'light',
              'bg-grey-800': postColor === null && colorScheme === 'dark',
            },
            props.source === 'expanded_view' && 'bg-grey-100  dark:bg-grey-800',
            props.source === 'slideshow' && [darkMode ? 'bg-button-dark' : 'bg-button-light'],
          ],
        ]"
        data-testid="surfacePostPropertiesValue"
        v-text="prop.formattedValue"
      />
    </div>
    <div v-if="customLinkButtonProperty != null && linkButtonPropertyValue != null" class="py-1 flex justify-start">
      <dd class="min-w-0">
        <SurfacePostPropertiesLinkButton
          :name="customLinkButtonProperty.name"
          :url="linkButtonPropertyValue"
          :size="linkButtonSize"
          :dark-mode="darkMode"
          data-testid="surfacePostPropertiesLinkButton"
        />
      </dd>
    </div>
  </dl>
</template>
